import type {
    NewsOverviewItemData,
    NextLocale,
} from '@beat-venues/common-types';
import { ContentfulImage } from '@beat-venues/contentful-image';
import { type VariantProps, cva } from 'class-variance-authority';
import { useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { ButtonOrLink, Heading, Paragraph, LogoTag } from '../../';
import type { CommonProps } from '../../types';
import { getEventDetailLink } from '../../utils/domain-util';

export interface NewsCardProps
    extends CommonProps,
        VariantProps<typeof NewsCardStyles>,
        Omit<NewsOverviewItemData, 'id'> {
    venueName?: string;
    isLarge?: boolean;
    isFirstThree?: boolean;
}

export const NewsCardStyles = cva(
    'relative group/newscard click-area group-[.is-highlighted]:first:flex group-[.is-highlighted]:first:flex-col group-[.is-highlighted]:first:items-stretch'
);

export const NewsCard = ({
    className,
    title,
    image,
    date,
    isLarge,
    slug,
    venueId,
    isFirstThree,
    testId = 'news-card',
    ...props
}: NewsCardProps): JSX.Element => {
    const { t, i18n } = useTranslation();

    const [venueName, setVenueName] = useState('');

    const getHref = () => {
        return getEventDetailLink(
            'beat-tickets',
            slug,
            i18n.language as NextLocale,
            'news'
        );
    };

    useEffect(() => {
        const venueTest = t(`venues.${venueId}.name`, 'global');
        setVenueName(venueTest);
    }, [t, venueId]);

    return (
        <div
            className={twMerge(NewsCardStyles({ className }))}
            {...props}
            data-testid={testId}
        >
            <div className="flex overflow-hidden rounded-md group-[.is-highlighted]:first:basis-full">
                {image && image.fields.image.fields.file ? (
                    <ContentfulImage
                        alt={image && image.fields.altText}
                        aspectRatio={isLarge ? undefined : 'BANNER'}
                        className={`h-full w-full object-cover transition group-hover/newscard:scale-105 group-[.is-highlighted]:first:max-h-full group-[.is-highlighted]:first:max-w-full group-[.is-highlighted]:first:grow-[2] group-[.is-highlighted]:first:object-cover ${
                            isLarge && 'md:aspect-banner md:object-cover'
                        }`}
                        src={image.fields.image.fields.file.url}
                    />
                ) : (
                    <div className="aspect-banner object-cover"></div>
                )}
            </div>

            <div className="flex flex-grow flex-col justify-between">
                <LogoTag
                    className={`group-[.is-highlighted] absolute -left-2 -top-2 ${
                        isFirstThree ? 'lg:-left-3 lg:-top-3' : ''
                    } ${
                        isLarge
                            ? 'group-[.is-highlighted]:first:-left-2 group-[.is-highlighted]:first:-top-2 lg:group-[.is-highlighted]:first:-left-4 lg:group-[.is-highlighted]:first:-top-4'
                            : ''
                    }`}
                    size={isLarge ? 'xl' : isFirstThree ? 'lg' : 'md'}
                    venueId={venueId}
                />

                <div>
                    {venueName && (
                        <Paragraph className="mb-0 mt-3 uppercase">
                            {venueName}
                        </Paragraph>
                    )}
                    {title && (
                        <ButtonOrLink
                            className="click-area-link"
                            href={getHref()}
                        >
                            <Heading
                                className="mb-0 mt-1 line-clamp-2 text-left group-hover/newscard:underline md:h-12"
                                size="h5"
                            >
                                {title}
                            </Heading>
                        </ButtonOrLink>
                    )}
                    {date && (
                        <Paragraph className="mb-0 mt-1.5">{date}</Paragraph>
                    )}
                </div>
            </div>
        </div>
    );
};
